import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { LayoutSingleColumn, Page, NamedLink, ExternalLink } from '../../components';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { hasRetreatListingType, RETREAT_LISTING_TYPE } from '../../config/configRetreat';
import { getRetreats, getRetreatBySlug, getStrapiMediaUrl } from '../../util/strapiApi';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import css from './RetreatsPage.module.css';

const Dates = ({ retreat }) => (
  <p className={css.dates}>
    <time dateTime={retreat.startDate}>{retreat.startDate}</time>
    {retreat.endDate !== retreat.startDate ? (
      <>
        {' '}
        – <time dateTime={retreat.endDate}>{retreat.endDate}</time>
      </>
    ) : null}
  </p>
);

const Detail = ({ retreat }) => {
  const cover = getStrapiMediaUrl(retreat.coverImage);
  const bookingUrl = retreat.registrationUrl || retreat.websiteUrl;
  const sections = [
    ['About this retreat', retreat.description],
    ['Your host', [retreat.hostName, retreat.hostDescription].filter(Boolean).join('\n')],
    ['Accommodation', [retreat.hotel, retreat.accommodation].filter(Boolean).join('\n')],
    ["What's included", retreat.included],
    ["What's not included", retreat.notIncluded],
    ['Booking information', retreat.bookingInformation],
    ['Cancellation policy', retreat.cancellationPolicy],
  ];
  return (
    <>
      <NamedLink name="RetreatsPage">All retreats</NamedLink>
      <header className={css.header}>
        <h1>{retreat.title}</h1>
        <p>{retreat.destination}</p>
        <Dates retreat={retreat} />
        {retreat.shortDescription ? <p>{retreat.shortDescription}</p> : null}
      </header>
      {cover ? (
        <img
          className={css.cover}
          src={cover}
          alt={retreat.coverImage?.alternativeText || retreat.title}
        />
      ) : null}
      <div className={css.details}>
        <div>
          {sections
            .filter(([, text]) => text)
            .map(([title, text]) => (
              <section key={title} className={css.section}>
                <h2>{title}</h2>
                <p className={css.text}>{text}</p>
              </section>
            ))}
          {retreat.gallery.length ? (
            <div className={css.gallery}>
              {retreat.gallery.map((media, index) => (
                <img
                  key={media.id || index}
                  loading="lazy"
                  src={getStrapiMediaUrl(media)}
                  alt={media.alternativeText || `${retreat.title} — photo ${index + 1}`}
                />
              ))}
            </div>
          ) : null}
        </div>
        <aside className={css.booking}>
          <h2>Plan your retreat</h2>
          {retreat.priceLabel ? <p className={css.price}>{retreat.priceLabel}</p> : null}
          {retreat.skillLevel ? <p>{retreat.skillLevel}</p> : null}
          {bookingUrl ? (
            <ExternalLink className={css.cta} href={bookingUrl}>
              View details & book with host
            </ExternalLink>
          ) : null}
          {retreat.contactEmail ? (
            <a href={`mailto:${retreat.contactEmail}`}>Contact the host</a>
          ) : null}
          {!bookingUrl && !retreat.contactEmail ? <p>Booking details coming soon.</p> : null}
        </aside>
      </div>
    </>
  );
};

const RetreatsPage = ({ params = {}, staticContext }) => {
  const slug = params.slug;
  const config = useConfiguration();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const [state, setState] = useState({ loading: true, error: false, data: null, slug });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ loading: true, error: false, data: null, slug });
    (slug ? getRetreatBySlug(slug) : getRetreats())
      .then(data => {
        if (active) setState({ loading: false, error: false, data, slug });
      })
      .catch(() => {
        if (active) setState({ loading: false, error: true, data: null, slug });
      });
    return () => {
      active = false;
    };
  }, [slug, attempt]);

  const loading = state.loading || state.slug !== slug;
  if (slug && !loading && !state.error && !state.data)
    return <NotFoundPage staticContext={staticContext} />;
  const title = slug && !loading && state.data ? state.data.title : 'Mahjong Retreats & Travel';
  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root}>
          {loading ? (
            <p role="status">Loading retreats…</p>
          ) : state.error ? (
            <div role="alert">
              <h1>Unable to load retreats</h1>
              <p>Please try again.</p>
              <button type="button" onClick={() => setAttempt(value => value + 1)}>
                Retry
              </button>
            </div>
          ) : slug ? (
            <Detail retreat={state.data} />
          ) : (
            <>
              <header className={css.header}>
                <h1>{title}</h1>
                <p>
                  Find your next mahjong getaway. Explore destinations, dates, and experiences from
                  retreat hosts.
                </p>
              </header>
              {hasRetreatListingType(config) ? (
                <p>
                  <NamedLink
                    name="SearchPageWithListingType"
                    params={{ listingType: RETREAT_LISTING_TYPE }}
                  >
                    Browse community retreat listings
                  </NamedLink>
                </p>
              ) : null}
              {state.data.length ? (
                <div className={css.grid}>
                  {state.data.map(retreat => {
                    const image = getStrapiMediaUrl(retreat.coverImage);
                    return (
                      <article className={css.card} key={retreat.documentId || retreat.id}>
                        <NamedLink name="RetreatPage" params={{ slug: retreat.slug }}>
                          {image ? (
                            <img
                              loading="lazy"
                              src={image}
                              alt={retreat.coverImage?.alternativeText || retreat.title}
                            />
                          ) : null}
                          <div className={css.cardBody}>
                            <h2>{retreat.title}</h2>
                            <p>{retreat.destination}</p>
                            <Dates retreat={retreat} />
                            {retreat.shortDescription ? <p>{retreat.shortDescription}</p> : null}
                            {retreat.priceLabel ? (
                              <p className={css.price}>{retreat.priceLabel}</p>
                            ) : null}
                          </div>
                        </NamedLink>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <section>
                  <h2>Coming Soon</h2>
                  <p>New mahjong retreats and travel experiences are on the way. Check back soon.</p>
                </section>
              )}
            </>
          )}
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

export default RetreatsPage;
